import { Check, Heart } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { OptInForm } from "@/components/OptInForm";

const GIVER_FIELDS = [
  { name: "first_name", label: "First Name", type: "text" },
  { name: "last_name", label: "Last Name", type: "text" },
  { name: "email", label: "Email Address", type: "email", full: true },
];

const TIERS = [
  {
    name: "Community Supporter",
    price: 10,
    tagline: "For neighbors who want to pitch in",
    features: ["Monthly cause updates", "Name on the community wall", "Launch-day thank-you letter"],
    testid: "giving-tier-supporter",
    highlight: false,
  },
  {
    name: "Cause Champion",
    price: 25,
    tagline: "For givers who want to move the needle",
    features: ["Quarterly stories from the field", "Early word on new causes", "Founding-member pin"],
    testid: "giving-tier-champion",
    highlight: true,
  },
  {
    name: "Founding Patron",
    price: 50,
    tagline: "For those building the legacy",
    features: ["Direct line to the causes you back", "Founding Patron certificate", "Annual gathering invitation"],
    testid: "giving-tier-patron",
    highlight: false,
  },
];

export default function Give() {
  return (
    <div data-testid="give-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 lg:px-8 lg:pt-20">
        <Reveal>
          <Eyebrow>For givers</Eyebrow>
          <h1
            className="mt-4 max-w-3xl font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            data-testid="give-headline"
          >
            Your generosity{" "}
            <span className="italic text-crimson">changes everything.</span>
          </h1>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-7">
            <div
              className="max-w-2xl space-y-4 text-base leading-relaxed text-cocoa sm:text-lg"
              data-testid="gloria-intro"
            >
            <p>
              Giving should feel like love, not pressure or politics. Too
              often our generosity gets redirected, diluted, or decided by
              someone else — while the people closest to us go unseen.
            </p>
            <p>
              At MyHeartGives, we believe giving should return to what it
              truly is: personal, joyful, and deeply human.
            </p>
            <p>
              Hi, I'm Gloria Hartwell. I learn every cause with care, so when you give,
              you know exactly where your kindness lands. No middlemen. No
              committees. You choose where, you choose when, and 100% of your
              gift goes exactly where your heart intends.
            </p>
            <p>
              We may not be able to help every cause, but we can honor every
              story — and make sure your generosity reaches the people who
              need it most.
            </p>
            <p className="font-serif text-lg italic text-ink">
              Here, every giver is celebrated.
            </p>
            <p className="font-serif text-base italic text-ink">
              — Gloria Hartwell, Heart of Giving
            </p>
          </div>
            <a
              href="/causes"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
              data-testid="find-a-cause-cta"
            >
              Find a Cause <Heart className="h-4 w-4" fill="currentColor" />
            </a>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <figure
              className="overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl"
              data-testid="gloria-portrait"
            >
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/8adc5beadfe7417cb67885889c566dac_image.bin"
                alt="Gloria Hartwell, Heart of Giving at MyHeartGives"
                className="aspect-[4/5] w-full object-cover object-top sepia-[0.12]"
                loading="eager"
              />
              <figcaption className="flex items-center justify-between bg-white px-6 py-4">
                <span className="font-serif text-lg font-semibold text-ink">Gloria Hartwell</span>
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-gold">
                  Heart of Giving
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* TIERS */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-8" data-testid="giving-tiers">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={0.1 * i}>
              <div
                className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-200 hover:-translate-y-1.5 hover:shadow-lg ${
                  t.highlight
                    ? "border-gold bg-white shadow-md ring-1 ring-gold/50"
                    : "border-line bg-white"
                }`}
                data-testid={t.testid}
              >
                {t.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-4 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-ink">
                    Most loved
                  </span>
                )}
                <h3 className="font-serif text-xl font-semibold text-ink sm:text-2xl">
                  {t.name}
                </h3>
                <p className="mt-1 text-sm italic text-taupe">{t.tagline}</p>
                <p className="mt-5">
                  <span className="font-serif text-4xl font-bold text-crimson">
                    ${t.price}
                  </span>
                  <span className="text-sm text-cocoa">/month</span>
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-cocoa">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#waitlist"
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-200 ${
                    t.highlight
                      ? "bg-crimson text-ivory hover:bg-crimson-deep"
                      : "border-2 border-gold text-ink hover:bg-gold-soft"
                  }`}
                  data-testid={`${t.testid}-join-button`}
                >
                  Join at Launch
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="every-dollar-statement">
            <Heart className="h-8 w-8 text-crimson" fill="currentColor" />
            <p className="max-w-xl font-serif text-2xl font-bold italic leading-snug text-ink sm:text-3xl">
              "Every dollar goes directly to the cause you choose."
            </p>
            <p className="text-sm text-cocoa">
              No middlemen. No mystery. Just your heart, delivered.
            </p>
          </div>
        </Reveal>
      </section>

      {/* GIVERS OPT-IN */}
      <section id="waitlist" className="bg-crimson" data-testid="giver-waitlist-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <p className="text-center font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold-bright">
              Opening Soon
            </p>
            <div className="mt-8">
              <OptInForm
                formId="givers-optin"
                heading="Join as a Giver"
                subheading="Be first through the door — Gloria Hartwell will make sure you feel celebrated from day one."
                source="givers"
                fields={GIVER_FIELDS}
                buttonLabel="I'm In — Let's Give"
                thankYou="Welcome! Gloria will reach out with your first Giving Circle match."
                testidPrefix="givers-optin"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
